import { useCallback, useEffect, useState } from 'react';

import { getDashboard } from '../services/dashboards';
import { normalizeId } from '../utils/ids';

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/**
 * One dashboard for its page (charts A6, plan §6.5): its read, as the
 * caller sees it in the context collection — its tiles judged, each chart
 * tile `available` with its chart or not, `canEdit`, the `version` a layout
 * save states.
 *
 *   status   'loading' · 'ready' · 'gone' (404, a malformed id's 400 — "gone",
 *            "not yours" and "unreadable" are one answer) · 'error'
 *   replace  hands the page's own write back (a rename, a pin, a save, the
 *            dashboard a 409 carried) so the page shows what the server
 *            answered without a second read
 *   reload   asks again (Try again)
 *
 * Another dashboard never shows the last one's name meanwhile: a change of
 * id clears what is held, unless it is the same dashboard.
 */
export default function useDashboard(collectionId, rawDashboardId, { enabled = true } = {}) {
	const dashboardId = OBJECT_ID.test(rawDashboardId || '') ? rawDashboardId : null;
	const [dashboard, setDashboard] = useState(null);
	const [status, setStatus] = useState('loading');
	const [loadToken, setLoadToken] = useState(0);

	useEffect(() => {
		if (!collectionId || !enabled) return undefined;
		if (!dashboardId) {
			setDashboard(null);
			setStatus('gone');
			return undefined;
		}
		let active = true;
		setStatus('loading');
		setDashboard((current) => (current && normalizeId(current._id) === dashboardId ? current : null));
		getDashboard(collectionId, dashboardId)
			.then((data) => {
				if (!active) return;
				setDashboard(data);
				setStatus('ready');
			})
			.catch((e) => {
				if (!active) return;
				setDashboard(null);
				const code = e?.response?.status;
				setStatus(code === 404 || code === 400 ? 'gone' : 'error');
			});
		return () => { active = false; };
	}, [collectionId, dashboardId, enabled, loadToken]);

	const replace = useCallback((next) => {
		if (!next || typeof next !== 'object') return;
		setDashboard(next);
		setStatus('ready');
	}, []);

	const reload = useCallback(() => setLoadToken((n) => n + 1), []);

	return { dashboard, status, dashboardId, replace, reload };
}
